
import { Component, Vue, Provide } from "vue-property-decorator"
import { Mutation } from "vuex-class"

import { Settings } from "luxon"

import { ReportData, ReportDetails } from "@/types/report"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { CalculationDataTypeValue, CalculationType } from "@/types/field"
import { dataTypeByFieldType } from "@/types"

import ReportSection from "@/components/Reports/ReportSection.vue"

import UserImageProvider from "@/services/userImageProvider"
import reportApi from "@/services/api/api_report"
import setBrandingLink from "@/scripts/brandingLink"

@Component({
	components: {
		"report-section": ReportSection
	}
})
export default class ReportView extends Vue {
	// --- Injection ---
	@Provide("userImageProvider") userImageProvider = new UserImageProvider()

	// --- VueX ---

	@Mutation setFeatures!: (features: { [key: string]: boolean }) => void

	// --- Data ---

	reportData: ReportData | null = null

	// --- Computed Properties ---

	public get params () {
		return this.$route.params
	}

	public get reportDataId (): string | null {
		if (!this.params) return null
		return this.params.reportDataId || null
	}

	get report (): ReportDetails | null {
		return this.reportData?.report || null
	}

	get columns (): { [key: string]: CaseColumn } {
		return [
			...caseColumns,
			...(this.reportData
				? this.reportData.fields.map(f => {
					return {
						label: f.title,
						description: f.description,
						key: "fields." + f.id,
						fieldId: f.id,
						dataType: f.dataType!,
						definitionId: f.definitionId,
						dataSourceId: f.dataSourceId,
						dynamicField: true,
						fieldExtensions: f.extensions,
						fieldDataType: f.dataType === "calculated"
							? CalculationDataTypeValue[f.extensions!.calculationType as CalculationType]
							: f.dataType && dataTypeByFieldType[f.dataType!] !== undefined
								? dataTypeByFieldType[f.dataType!]
								: undefined
					}
				})
				: [])
		].toDictionary("key")
	}

	// --- Lifecycle Hooks ---

	async mounted () {
		if (this.reportDataId) {
			const reportData = await reportApi.reportData(this.reportDataId)
			this.userImageProvider.setValues(reportData.userPictureUrls)
			this.setFeatures(reportData.tenantFeatures);

			(Settings as any).defaultZone = reportData.timeZoneIana ? reportData.timeZoneIana : "system"

			if (reportData.data) {
				Object.values(reportData.data).forEach(i => {
					i.insight.calculations.forEach(calculation => {
						Vue.set(calculation, "groupByKeyLabels", i.results.calculationGroupByLabels[calculation.key])
					})
				})
			}

			if (reportData.report.tenantId) {
				setBrandingLink(reportData.report.tenantId)
			}

			this.reportData = reportData
		}

		if ((window as any).ironpdf) {
			setTimeout(() => {
				(window as any).ironpdf.notifyRender()
			}, 3000)
		}
	}
}
